import { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useApiClient } from "../api/useApiClient";
import type { ReviewItem, ProyectoCatalogo } from "../types/reviewItem";
import { ProyectoCombobox, proyectoOk } from "../components/ProyectoCombobox";
import { LineasRow, LineasToggle } from "../components/LineasRow";
import { NotasAdjuntoRow, NotasToggle } from "../components/NotasAdjuntoRow";
import { useAdjunto } from "../hooks/useAdjunto";
import { CfeIdentidad } from "../components/CfeIdentidad";
import { TablaToolbar } from "../components/TablaToolbar";
import { Paginador } from "../components/Paginador";
import { ThOrdenable } from "../components/ThOrdenable";
import { ModalMotivo } from "../components/ModalMotivo";
import { abrirEnNuevaPestana } from "../utils/abrirEnNuevaPestana";
import { formatMonto, formatFecha } from "../utils/formato";
import { useTablaBandeja } from "../hooks/useTablaBandeja";
import { useAccion } from "../hooks/useAccion";
import { AvisoError } from "../components/AvisoError";
import { useContadores } from "../contexts/ContadoresContext";
import { useHasRole } from "../auth/useRoles";
import { ROLE_COMPRADOR, ROLE_CONTADURIA } from "../constants/roles";

// Las columnas cambian por rol: quien NO es Aprobador ve esta página en modo
// seguimiento (sin checkbox de lote ni celda de comentario). Ver el comentario
// de `esAprobador` más abajo.
// [AMPLIADO 19/08/2026] +2 para el Aprobador: Nro. de Carpeta y Fecha de
// llegada, obligatorios solo para cuentas de Importaciones (item.es_importacion).
// [AMPLIADO 02/09/2026] +1: Proyecto, OPCIONAL, solo para cuentas con
// item.requiere_proyecto.
// [FUSIONADAS 08/09/2026] Esas 3 columnas pasaron a UNA sola ("Datos
// requeridos"), 12 -> 10. Medido sobre las 66 filas reales de la bandeja: 38
// piden Carpeta+Fecha, 7 piden Proyecto, 21 no piden nada y CERO piden las dos
// cosas (38+7 = 66-21) — son mutuamente excluyentes porque Importaciones es 1
// cuenta y Proyecto son 13 de Marketing, distintas. Con 3 columnas de 8-9% el
// placeholder del input de Carpeta se veía cortado (table-layout: fixed +
// overflow: hidden); con una sola de 19% entra completo. La celda igual tolera
// el caso "las dos" apilándolas, para no romperse si alguna cuenta queda
// marcada con ambos flags.
// [FUSIONADAS 08/09/2026, segunda ronda] La columna Comentario salió de la
// tabla, 10 -> 9. Vivía siempre abierta (textarea de 44px de alto mínimo + su
// botón, ~85px en CADA fila) y era lo que más estiraba la fila; ahora vive en
// la fila expandible de Notas junto con el comprobante adjunto. Criterio: lo
// OBLIGATORIO para autorizar queda inline (Carpeta/Fecha/Proyecto), lo
// OPCIONAL que nunca bloquea queda a un click.
const COLUMNAS_APROBADOR = 9;
const COLUMNAS_SEGUIMIENTO = 7;

// Validado también en el backend (autorizar_comprador) — esto es solo un
// control blando para no invitar a un POST que el backend va a rechazar.
const REGEX_NUMERO_CARPETA = /^[A-Za-z0-9]{8,12}$/;

// [NUEVO 09/09/2026] Filtro "Mis asignadas"/"Todas" — solo aplica a quien
// tiene AMBOS roles (Contaduria + Comprador): el backend ya le manda TODA la
// bandeja (ver get_review_items en function_app.py, `ROL_CONTADURIA in roles`
// -> sin filtrar por comprador_asignado), a diferencia de un Aprobador con
// solo el rol Comprador, a quien el backend ya le filtra a lo suyo. Pedido de
// Liber Almeida: para Daniel Rama/Carina Rama y demás con los dos roles, ver
// TODO por default resultaba confuso — el default acá pasa a ser "Mis
// asignadas", con "Todas" disponible con un click. Es un filtro 100% de
// cliente (los datos ya llegaron todos) — no hace falta tocar el backend.
export type FiltroAsignacion = "mias" | "todas";

const TABS_ASIGNACION: Array<{ clave: FiltroAsignacion; etiqueta: string }> = [
  { clave: "mias", etiqueta: "Mis asignadas" },
  { clave: "todas", etiqueta: "Todas" },
];

/** Comparación case-insensitive: es un filtro de UI, no la barrera de
 *  seguridad real (esa es `comprador_asignado = ?` en el backend, que se
 *  apoya en la collation case-insensitive de la base). */
export function aplicarFiltroAsignacion(
  items: ReviewItem[],
  filtro: FiltroAsignacion,
  miUpn: string | undefined
): ReviewItem[] {
  if (filtro === "todas" || !miUpn) return items;
  const upn = miUpn.toLowerCase();
  return items.filter((item) => item.comprador_asignado?.toLowerCase() === upn);
}

/**
 * Paso 2 — Aprobador (value real del rol/token: "Comprador", ver
 * src/constants/roles.ts). Filtra PENDIENTE_COMPRADOR.
 *
 * Regla clave (ver CLAUDE.md del backend): el comentario es una acción
 * INDEPENDIENTE de autorizar — por eso hay dos botones separados, no un
 * solo submit. No fusionarlos.
 *
 * [REESTRUCTURADA 08/08/2026] Mismo bloque de identificación del CFE que las
 * otras bandejas (CfeIdentidad + Monto + Fecha), y se quitó la columna Estado
 * (redundante: esta pantalla filtra un único estado).
 */
export function CompradorPage() {
  const api = useApiClient();
  // [NUEVO 11/08/2026] Contaduría también entra a esta página, en modo
  // SEGUIMIENTO. El backend ya lo permitía —`get_review_items?paso=comprador`
  // acepta Contaduría y le devuelve TODO, no solo lo asignado a una persona—
  // pero el Frontend no le ofrecía el link, así que Contaduría no tenía forma
  // de ver qué quedó esperando al Aprobador. Detectado por Micaela Morales
  // (Contaduría) al no encontrar la página.
  //
  // Lo que NO se le ofrece: Autorizar, el lote y el comentario. No es una
  // decisión de UI: el backend exige rol `Comprador` en esos tres endpoints y
  // devolvería 403. Y responde a una regla de negocio explícita — "no existe
  // Contaduría autoriza en nombre del Comprador" (CLAUDE.md), que necesita
  // aval del Comité para cambiar.
  //
  // Rechazar SÍ se le ofrece: el backend lo acepta para ambos roles, y hoy
  // Contaduría no tenía NINGUNA forma desde la UI de rechazar una factura que
  // ya pasó al Paso 2 — el 11/08 hubo que hacerlo por API para 3 recibos de
  // cobranza detectados después de autorizar.
  const esAprobador = useHasRole(ROLE_COMPRADOR);
  const esContaduria = useHasRole(ROLE_CONTADURIA);
  // Ver el comentario de TABS_ASIGNACION más arriba: solo quien tiene los dos
  // roles recibe TODA la bandeja del backend, así que solo a esas personas
  // les hace falta (y les sirve) este filtro.
  const puedeElegirAsignacion = esAprobador && esContaduria;
  const { accounts } = useMsal();
  const miUpn = accounts[0]?.username;
  const [filtroAsignacion, setFiltroAsignacion] = useState<FiltroAsignacion>("mias");
  const COLUMNAS = esAprobador ? COLUMNAS_APROBADOR : COLUMNAS_SEGUIMIENTO;
  const { refrescar: refrescarContadores } = useContadores();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  // [NUEVO 08/09/2026] Independiente de `expandidos` (Líneas) — abrir una no
  // cierra la otra.
  const [notasAbiertas, setNotasAbiertas] = useState<Set<string>>(new Set());
  const [comentarios, setComentarios] = useState<Record<string, string>>({});
  // [NUEVOS 19/08/2026] Solo se piden/editan para cuentas de Importaciones
  // (item.es_importacion) — ver REGEX_NUMERO_CARPETA y datosImportacionOk.
  const [carpetas, setCarpetas] = useState<Record<string, string>>({});
  const [fechasLlegada, setFechasLlegada] = useState<Record<string, string>>({});
  // [NUEVOS 02/09/2026] Proyecto — solo se ofrece para cuentas con
  // item.requiere_proyecto, y es OPCIONAL (ver proyectoOk).
  const [proyectosElegidos, setProyectosElegidos] = useState<Record<string, string>>({});
  const [catalogoProyectos, setCatalogoProyectos] = useState<ProyectoCatalogo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rechazando, setRechazando] = useState<ReviewItem | null>(null);

  const visibles = useMemo(
    () => (puedeElegirAsignacion ? aplicarFiltroAsignacion(items, filtroAsignacion, miUpn) : items),
    [items, filtroAsignacion, miUpn, puedeElegirAsignacion]
  );

  // Una pasada por cambio de `items`, no una por tab en cada render — mismo
  // criterio que cantidadesPago en FacturasParaPagarPage.tsx.
  const cantidadesAsignacion = useMemo(() => {
    if (!puedeElegirAsignacion) return { mias: 0, todas: 0 };
    return {
      mias: aplicarFiltroAsignacion(items, "mias", miUpn).length,
      todas: items.length,
    };
  }, [items, miUpn, puedeElegirAsignacion]);

  const tabla = useTablaBandeja(visibles, {
    camposBusqueda: ["proveedor_nombre", "rut_emisor", "nro", "serie", "comprador_asignado"],
    ordenInicial: { columna: "fecha_documento", direccion: "desc" },
    camposNumericos: ["monto"],
    claveEstable: "id",
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reviewItems = await api.listarReviewItems("comprador", "pendientes");
      setItems(reviewItems);
      // [NUEVO 02/09/2026] El catálogo de proyectos va en su PROPIO try: es la
      // primera vez que esta página consume un catálogo, y un fallo acá no
      // puede vaciar la bandeja entera. Sin catálogo, el desplegable queda sin
      // opciones y el proyecto no se puede elegir — pero autorizar sigue
      // funcionando, justamente porque el dato es opcional.
      try {
        setCatalogoProyectos(await api.listarCatalogoProyectos());
      } catch {
        setCatalogoProyectos([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido al cargar la bandeja.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const recargarTodo = useCallback(async () => {
    setSelected(new Set());
    await cargar();
    refrescarContadores();
  }, [cargar, refrescarContadores]);

  const accion = useAccion(recargarTodo);
  const subirAdjunto = useAdjunto(accion);

  function toggleSelected(id: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  /** Solo la página visible, no la bandeja completa. */
  function toggleSelectedPagina(checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const item of tabla.filas) {
        if (checked) next.add(item.id);
        else next.delete(item.id);
      }
      return next;
    });
  }

  function toggleExpandido(id: string) {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleNotas(id: string) {
    setNotasAbiertas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** El valor efectivo de Carpeta/Llegada: lo editado localmente o lo que ya
   *  vino guardado (por si se recarga la página a mitad de completar). */
  function carpetaDe(item: ReviewItem): string {
    return carpetas[item.id] ?? item.numero_carpeta ?? "";
  }
  function fechaLlegadaDe(item: ReviewItem): string {
    return fechasLlegada[item.id] ?? item.fecha_llegada ?? "";
  }
  function proyectoDe(item: ReviewItem): string {
    return proyectosElegidos[item.id] ?? item.proyecto_asignado ?? "";
  }

  /** El proyecto es OPCIONAL: vacío es válido. Lo único que bloquea es un
   *  texto que no corresponda a ningún proyecto del catálogo — SAP rechaza el
   *  documento entero con ODBC -2028 si no conoce el ProjectCode. */
  function proyectoDeLaFilaOk(item: ReviewItem): boolean {
    if (!item.requiere_proyecto) return true;
    return proyectoOk(proyectoDe(item), catalogoProyectos);
  }

  /** Cuentas que NO son de Importaciones no piden nada — siempre válido.
   *  Para las que sí, hace falta Carpeta con el formato correcto y una fecha. */
  function datosImportacionOk(item: ReviewItem): boolean {
    if (!item.es_importacion) return true;
    return REGEX_NUMERO_CARPETA.test(carpetaDe(item)) && Boolean(fechaLlegadaDe(item));
  }

  // Todas las acciones pasan por `accion.ejecutar`: refresca la lista SIEMPRE
  // (haya funcionado o no) y muestra el error sin destruir la tabla. Ver
  // useAccion.ts y el incidente del 10/08/2026 en docs/Decisions.md.
  async function guardarComentario(id: string) {
    const comentario = comentarios[id];
    if (!comentario) return;
    // recargarDespues: false — comentar no cambia el estado ni nada de lo que
    // la tabla muestra, refrescar seria trabajo al vacio. Ver docstring.
    await accion.ejecutar(() => api.comentarioComprador(id, comentario), {
      descripcion: "guardar el comentario",
      recargarDespues: false,
    });
  }

  async function autorizar(id: string, item: ReviewItem) {
    const payload: Parameters<typeof api.autorizarComprador>[1] = { comentario: comentarios[id] };
    if (item.es_importacion) {
      payload.numero_carpeta = carpetaDe(item);
      payload.fecha_llegada = fechaLlegadaDe(item);
    }
    // [NUEVO 02/09/2026] Solo se manda si la cuenta lo pide Y el Aprobador
    // eligió algo. Si no eligió nada la clave no viaja y la factura va a SAP
    // sin proyecto, igual que hasta hoy.
    if (item.requiere_proyecto && proyectoDe(item).trim()) {
      payload.proyecto = proyectoDe(item).trim();
    }
    const resultado = await accion.ejecutar(() => api.autorizarComprador(id, payload), {
      descripcion: "autorizar la factura",
    });
    // [NUEVO 19/08/2026] No es un error — la autorización ya se hizo. Pasa
    // con directores que tienen los dos roles a propósito (Contaduría +
    // Aprobador) y aprobaron ellos mismos el Paso 1 de esta factura.
    if (resultado?.advertencia) {
      accion.mostrarAviso(resultado.advertencia);
    }
  }

  async function confirmarRechazo(motivo: string) {
    if (!rechazando) return;
    const id = rechazando.id;
    setRechazando(null);
    await accion.ejecutar(() => api.rechazar(id, { motivo, origen: "comprador" }), {
      descripcion: "rechazar la factura",
    });
  }

  async function autorizarSeleccionadas() {
    if (selected.size === 0) return;
    const resultado = await accion.ejecutar(
      () => api.autorizarCompradorLote({ ids: Array.from(selected) }),
      { descripcion: `autorizar las ${selected.size} facturas seleccionadas` }
    );
    // [NUEVO 19/08/2026] El lote no acepta Nro. de Carpeta/Fecha de llegada
    // por ítem, así que una cuenta de Importaciones nunca se autoriza acá —
    // el backend la salta y la reporta, hay que avisarlo en vez de que
    // parezca que se procesaron todas.
    const avisos: string[] = [];
    if (resultado?.sin_datos_importacion?.length) {
      avisos.push(
        `${resultado.sin_datos_importacion.length} quedaron sin autorizar por ser de Importaciones ` +
          `(necesitan Nro. de Carpeta y Fecha de llegada, que no se pueden cargar en lote) — autorizalas una por una.`
      );
    }
    // [NUEVO 19/08/2026] Estas SÍ se autorizaron — no bloquea. Pasa con
    // directores con los dos roles que también hicieron el Paso 1.
    if (resultado?.mismo_aprobador?.length) {
      avisos.push(
        `${resultado.mismo_aprobador.length} las autorizaste vos mismo en los dos pasos ` +
          `(Contaduría y Aprobador).`
      );
    }
    if (avisos.length > 0) {
      accion.mostrarAviso(`Se autorizaron ${resultado!.procesados}. ${avisos.join(" ")}`);
    }
  }

  async function abrirDocumento(id: string, tipo: "xml" | "pdf" | "adjunto") {
    try {
      const url = await api.obtenerBlobSasUrl(id, tipo);
      abrirEnNuevaPestana(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`No se pudo obtener el ${tipo.toUpperCase()}:`, err);
      window.alert(`No se pudo abrir el ${tipo.toUpperCase()}. Ver consola para el detalle.`);
    }
  }

  if (loading) return <p>Cargando bandeja...</p>;
  if (error) return <p className="error-text">Error: {error}</p>;

  const todasLaPaginaSeleccionadas =
    tabla.filas.length > 0 && tabla.filas.every((i) => selected.has(i.id));

  return (
    <div>
      <h1>{esAprobador ? "Facturas para tu autorización" : "Pendientes del Aprobador"}</h1>
      <p className="subtitle">
        {esAprobador
          ? "Paso 2 de 2: solo ves facturas ya revisadas y autorizadas por Contaduría."
          : "Seguimiento: facturas que Contaduría ya autorizó y están esperando a su Aprobador. " +
            "Desde acá no se autoriza (eso es del Aprobador), pero sí se puede rechazar."}
      </p>

      {puedeElegirAsignacion && (
        <div className="filtro-tabs" role="group" aria-label="Filtrar por asignación">
          {TABS_ASIGNACION.map(({ clave, etiqueta }) => (
            <button
              key={clave}
              type="button"
              aria-pressed={filtroAsignacion === clave}
              className={`filtro-tab${filtroAsignacion === clave ? " filtro-tab-activo" : ""}`}
              onClick={() => setFiltroAsignacion(clave)}
            >
              {etiqueta} <span className="filtro-tab-cantidad">{cantidadesAsignacion[clave]}</span>
            </button>
          ))}
        </div>
      )}

      <TablaToolbar
        busqueda={tabla.busqueda}
        onBuscar={tabla.setBusqueda}
        filtradas={tabla.filtradas}
        totalSinFiltro={visibles.length}
      />

      <AvisoError mensaje={accion.error} onCerrar={accion.limpiarError} />

      {esAprobador && selected.size > 0 && (
        <div className="bulk-bar">
          <span>{selected.size} factura(s) seleccionada(s)</span>
          <button disabled={accion.enCurso} onClick={autorizarSeleccionadas}>
            {accion.enCurso ? "Procesando..." : "Autorizar seleccionadas"}
          </button>
        </div>
      )}

      <table className="compact">
        {/* [REBALANCEADO 08/09/2026] Las 3 columnas condicionales (Carpeta,
            Llegada, Proyecto) se fusionaron en una sola de 19%, y Acciones
            pasó de 9% a 15%: era el contenido más gordo (5 botones) en la
            columna más flaca, y con flex-wrap terminaba en ~4 renglones
            apilados — la causa principal de que la fila midiera ~146px.
            [AJUSTADO el mismo día, mirando la pantalla real] "Datos
            requeridos" quedó ancha de más con 19%: se le sacaron 4 puntos y
            fueron a Cuenta contable (9->12, partía "1-1-5-001-301" en dos
            líneas) y a Proveedor (11->12).
            [REBALANCEADO otra vez el mismo día] Al sacar la columna Comentario
            (que se fue a la fila de Notas) quedaron 10 puntos libres: fueron a
            Proveedor (12->13), Monto (8->9), Fecha (7->8), Aprobador (8->9),
            Datos requeridos (15->17) y Acciones (15->19), que ahora tiene un
            botón más (Notas).
            Las dos ramas siguen sumando exactamente 100%:
            Aprobador 3+10+13+9+8+12+9+17+19, seguimiento 16+18+12+10+14+13+17. */}
        <colgroup>
          {esAprobador && <col style={{ width: "3%" }} />}
          <col style={{ width: esAprobador ? "10%" : "16%" }} />
          <col style={{ width: esAprobador ? "13%" : "18%" }} />
          <col style={{ width: esAprobador ? "9%" : "12%" }} />
          <col style={{ width: esAprobador ? "8%" : "10%" }} />
          <col style={{ width: esAprobador ? "12%" : "14%" }} />
          <col style={{ width: esAprobador ? "9%" : "13%" }} />
          {esAprobador && <col style={{ width: "17%" }} />}
          <col style={{ width: esAprobador ? "19%" : "17%" }} />
        </colgroup>
        <thead>
          <tr>
            {esAprobador && (
            <th>
              <input
                type="checkbox"
                checked={todasLaPaginaSeleccionadas}
                title="Seleccionar todas las de esta pagina"
                onChange={(e) => toggleSelectedPagina(e.target.checked)}
              />
            </th>
            )}
            <ThOrdenable columna="nro" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Documento
            </ThOrdenable>
            <ThOrdenable columna="proveedor_nombre" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Proveedor
            </ThOrdenable>
            <ThOrdenable columna="monto" orden={tabla.orden} onOrdenar={tabla.alternarOrden} alineado="derecha">
              Monto
            </ThOrdenable>
            <ThOrdenable columna="fecha_documento" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Fecha doc.
            </ThOrdenable>
            <th>Cuenta contable</th>
            {/* [NUEVA 11/08/2026] Contaduría ve TODA esta bandeja (el filtro por
                asignación solo aplica a quien es solo Aprobador), así que sin
                esta columna no podía saber de quién estaba esperando cada
                factura. Para el propio Aprobador es redundante —siempre es él—
                pero inofensiva, y evita bifurcar la tabla por rol. */}
            <ThOrdenable columna="comprador_asignado" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Aprobador
            </ThOrdenable>
            {/* [MOVIDA 08/09/2026] La columna Comentario ya no está acá: la
                textarea siempre abierta era lo que más estiraba la fila.
                Ahora vive en la fila expandible de Notas (botón en Acciones),
                junto con el comprobante adjunto. */}
            {/* [FUSIONADA 08/09/2026] Antes eran 3 columnas separadas (N.
                Carpeta, F. Llegada, Proyecto). Cada fila pide una cosa o la
                otra, nunca las dos — ver el comentario de COLUMNAS_APROBADOR.
                Las filas que no piden nada muestran un solo "—" en vez de tres. */}
            {esAprobador && <th>Datos requeridos</th>}
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {tabla.filas.length === 0 && (
            <tr>
              <td colSpan={COLUMNAS} className="empty-row">
                {items.length === 0
                  ? (esAprobador ? "No hay facturas para autorizar." : "No hay facturas esperando al Aprobador.")
                  : visibles.length === 0
                  ? "No tenés facturas asignadas — probá la pestaña \"Todas\"."
                  : "Ninguna factura coincide con la busqueda."}
              </td>
            </tr>
          )}
          {tabla.filas.map((item) => (
            <Fragment key={item.id}>
              <tr>
                {esAprobador && (
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={(e) => toggleSelected(item.id, e.target.checked)}
                  />
                </td>
                )}
                <td>
                  <CfeIdentidad
                    tipoCfe={item.tipo_cfe}
                    serie={item.serie}
                    nro={item.nro}
                    rutEmisor={item.rut_emisor}
                  />
                </td>
                <td className="wrap-ok" title={item.proveedor_nombre ?? undefined}>
                  {item.proveedor_nombre}
                </td>
                <td className="mono celda-monto">
                  {formatMonto(item.monto)}
                  <span className="moneda">{item.moneda}</span>
                </td>
                <td className="mono">{formatFecha(item.fecha_documento)}</td>
                {/* Solo lectura acá — el Aprobador nunca edita cuenta, ver CLAUDE.md */}
                <td title={item.cuenta_vigente}>{item.cuenta_vigente}</td>
                <td className="wrap-ok" title={item.comprador_asignado ?? undefined}>
                  {item.comprador_asignado ? (
                    <span className="mono">{item.comprador_asignado.split("@")[0]}</span>
                  ) : (
                    "—"
                  )}
                </td>
                {/* [FUSIONADA 08/09/2026] Una sola celda para lo que la cuenta
                    exija: Carpeta+Llegada (Importaciones) o Proyecto. Si una
                    cuenta quedara marcada con los dos flags, se apilan — no se
                    asume exclusividad aunque hoy los datos reales lo sean. */}
                {esAprobador && (
                <td>
                  {item.es_importacion || item.requiere_proyecto ? (
                    <div className="datos-requeridos-cell">
                      {/* Boolean(...) NO es de más: estos dos flags están
                          tipados `boolean` pero en runtime llegan como 0/1
                          (la vista hace COALESCE(bit, 0) y por precedencia de
                          tipos de SQL Server eso devuelve INT, no BIT). Con
                          `{item.es_importacion && ...}` React renderiza un
                          "0" suelto en la celda cuando el flag es 0 —
                          `false`/`null` no se ven, el número 0 sí. Bug real
                          encontrado el 08/09/2026 en la primera mirada a la
                          pantalla; el código anterior usaba ternarios y por
                          eso nunca lo tuvo. */}
                      {Boolean(item.es_importacion) && (
                        <>
                          <input
                            type="text"
                            className="mono"
                            maxLength={12}
                            placeholder="N. Carpeta"
                            title="Nro. de Carpeta — alfanumérico, 8 a 12 caracteres"
                            value={carpetaDe(item)}
                            onChange={(e) => setCarpetas((prev) => ({ ...prev, [item.id]: e.target.value }))}
                          />
                          <input
                            type="date"
                            title="Fecha de llegada"
                            value={fechaLlegadaDe(item)}
                            onChange={(e) => setFechasLlegada((prev) => ({ ...prev, [item.id]: e.target.value }))}
                          />
                        </>
                      )}
                      {Boolean(item.requiere_proyecto) && (
                        <ProyectoCombobox
                          valor={proyectoDe(item)}
                          proyectos={catalogoProyectos}
                          onChange={(valor) =>
                            setProyectosElegidos((prev) => ({ ...prev, [item.id]: valor }))
                          }
                        />
                      )}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
                )}
                <td className="actions-cell">
                  {/* [AGRUPADOS 08/09/2026] Los 3 de consulta van juntos en su
                      propio flex para que caigan en UNA línea, en vez de que el
                      wrap del contenedor los reparta de a uno por renglón. */}
                  <div className="actions-docs">
                    <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "xml")}>
                      XML
                    </button>
                    {item.pdf_blob_url && (
                      <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "pdf")}>
                        PDF
                      </button>
                    )}
                    <LineasToggle expanded={expandidos.has(item.id)} onToggle={() => toggleExpandido(item.id)} />
                    {/* [NUEVO 08/09/2026] Comentario + comprobante adjunto.
                        Se ofrece siempre que haya algo para ver o hacer: el
                        Aprobador puede comentar y adjuntar; Contaduría en modo
                        seguimiento solo entra si ya hay un adjunto que mirar. */}
                    {(esAprobador || item.adjunto_blob_url) && (
                      <NotasToggle
                        expanded={notasAbiertas.has(item.id)}
                        tieneAdjunto={Boolean(item.adjunto_blob_url)}
                        onToggle={() => toggleNotas(item.id)}
                      />
                    )}
                  </div>
                  {/* Deshabilitados mientras corre: la accion puede tardar
                      varios segundos y los POST no son idempotentes. */}
                  {esAprobador && (
                    <button
                      disabled={!datosImportacionOk(item) || !proyectoDeLaFilaOk(item) || accion.enCurso}
                      /* [ACORTADO 08/09/2026] El label era "Autorizar → pasa a
                         SAP" y no entraba en una línea; la explicación no se
                         perdió, pasó al title (que ya se usaba para los
                         mensajes de validación). */
                      title={
                        !datosImportacionOk(item)
                          ? "Cuenta de Importaciones: Nro. de Carpeta (alfanumérico, 8-12) y Fecha de llegada son obligatorios"
                          : !proyectoDeLaFilaOk(item)
                          ? "Ese proyecto no está en el catálogo — elegí uno de la lista, o dejá el campo vacío"
                          : "Autoriza el Paso 2 — la factura queda lista para postearse en SAP"
                      }
                      onClick={() => autorizar(item.id, item)}
                    >
                      Autorizar
                    </button>
                  )}
                  <button
                    className="btn-danger"
                    disabled={accion.enCurso}
                    onClick={() => setRechazando(item)}
                  >
                    Rechazar
                  </button>
                </td>
              </tr>
              {expandidos.has(item.id) && <LineasRow reviewItemId={item.id} colSpan={COLUMNAS} />}
              {/* Independiente de la de Líneas: las dos pueden estar abiertas
                  a la vez, son <tr> con estados separados. */}
              {notasAbiertas.has(item.id) && (
                <NotasAdjuntoRow
                  item={item}
                  colSpan={COLUMNAS}
                  comentario={
                    esAprobador
                      ? {
                          valor: comentarios[item.id] ?? "",
                          onChange: (valor) =>
                            setComentarios((prev) => ({ ...prev, [item.id]: valor })),
                          onGuardar: () => guardarComentario(item.id),
                        }
                      : undefined
                  }
                  // Solo el Aprobador sube en SU paso. Contaduría entra en modo
                  // seguimiento y solo mira — el backend le daría 403 igual.
                  puedeAdjuntar={esAprobador}
                  onElegirArchivo={(file) => subirAdjunto(item.id, file)}
                  onVerAdjunto={() => abrirDocumento(item.id, "adjunto")}
                  enCurso={accion.enCurso}
                />
              )}
            </Fragment>
          ))}
        </tbody>
      </table>

      <Paginador
        pagina={tabla.pagina}
        totalPaginas={tabla.totalPaginas}
        desde={tabla.desde}
        hasta={tabla.hasta}
        total={tabla.total}
        onIrAPagina={tabla.irAPagina}
      />

      <ModalMotivo
        abierto={rechazando !== null}
        titulo="Rechazar factura"
        detalle={
          rechazando
            ? `${rechazando.proveedor_nombre ?? rechazando.rut_emisor} — ${rechazando.serie}-${rechazando.nro}`
            : undefined
        }
        onConfirmar={confirmarRechazo}
        onCancelar={() => setRechazando(null)}
      />
    </div>
  );
}
